import React, { FC, useState, Fragment } from 'react'
import {
  Grid,
  Divider,
} from '@mui/material'
import {
  useDeleteBuildingTypeRequest,
} from 'src/hooks/api'
import { BuildingTypeList as ApiBuildingTypeList } from 'src/api'
import { useAddPopupMessage, useDialog } from 'src/hooks/ui'
import { DialogNames } from 'src/enums'

import { InlineBuildingType } from './inlineBuildingType'
import { EditableBuildingType } from './editableBuildingType'
import { RemoveBuildingTypeDialog } from './removeBuildingTypeDialog'

interface BuildingTypeListProps {
  data: ApiBuildingTypeList[];
  onChange: () => void;
}

export const BuildingTypeList: FC<BuildingTypeListProps> = ({ data, onChange }) => {
  const [editId, setEditId] = useState<number | null>(null)
  const [removeItem, setRemoveItem] = useState<{
    id: number;
    name: string;
  } | null>(null)
  const addMessage = useAddPopupMessage()
  const { openDialog, closeDialog } = useDialog(DialogNames.DeleteBuildingType)

  const removeBuildingTypeRequest = useDeleteBuildingTypeRequest()

  const openRemoveDialog = (type: { id: number; name: string }) => {
    setRemoveItem(type)
    openDialog()
  }

  const closeRemoveDialog = () => {
    closeDialog(() => setRemoveItem(null))
  }

  const onRemove = () => {
    if (removeItem !== null) {
      removeBuildingTypeRequest.mutate(
        { id: removeItem.id },
        {
          onSuccess: onChange,
          onError: (error) =>
            addMessage({ type: 'error', text: error.message }),
        }
      )
      closeRemoveDialog()
    }
  }

  const updateEditId = (id: number | null) => {
    setEditId(id)
  }

  const saveChanges = () => {
    updateEditId(null)
    onChange()
  }

  return (
    <>
      <Grid container direction="column" spacing={2}>
        {data.map(({ id, displayName, projectsCount }, idx, list) => (
          <Fragment key={idx}>
            <Grid item container spacing={1} alignItems="center">
              {editId === id ? (
                <EditableBuildingType
                  id={id}
                  name={displayName || ''}
                  onChange={saveChanges}
                  onCancel={() => updateEditId(null)}
                />
              ) : (
                <InlineBuildingType
                  id={id}
                  name={displayName || ''}
                  canChange={projectsCount === 0}
                  onEdit={updateEditId}
                  onDelete={openRemoveDialog}
                />
              )}
            </Grid>
            {idx !== list.length - 1 && (
              <Divider flexItem={true} sx={{ ml: 2, mt: 2 }} />
            )}
          </Fragment>
        ))}
      </Grid>
      <RemoveBuildingTypeDialog
        name={removeItem?.name || ''}
        onDelete={onRemove}
        onCancel={closeRemoveDialog}
      />
    </>
  )
}